import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type ClockDef = { label: string; city: string; tz: string };

// Shown inline in the command bar — 8 highest-relevance zones for gov contracting
const BAR_CLOCKS: ClockDef[] = [
  { label: 'DC', city: 'Washington', tz: 'America/New_York' },
  { label: 'LON', city: 'London', tz: 'Europe/London' },
  { label: 'BRU', city: 'Brussels', tz: 'Europe/Brussels' },
  { label: 'CAI', city: 'Cairo', tz: 'Africa/Cairo' },
  { label: 'RUH', city: 'Riyadh', tz: 'Asia/Riyadh' },
  { label: 'UAE', city: 'Dubai', tz: 'Asia/Dubai' },
  { label: 'DEL', city: 'New Delhi', tz: 'Asia/Kolkata' },
  { label: 'TYO', city: 'Tokyo', tz: 'Asia/Tokyo' },
];

// Full 5×5 grid shown in hover card (west → east)
const GRID_CLOCKS: ClockDef[] = [
  { label: 'UTC', city: 'UTC', tz: 'UTC' },
  { label: 'ANC', city: 'Anchorage', tz: 'America/Anchorage' },
  { label: 'LAX', city: 'Los Angeles', tz: 'America/Los_Angeles' },
  { label: 'DEN', city: 'Denver', tz: 'America/Denver' },
  { label: 'CHI', city: 'Chicago', tz: 'America/Chicago' },
  { label: 'DC', city: 'Washington', tz: 'America/New_York' },
  { label: 'NYC', city: 'New York', tz: 'America/New_York' },
  { label: 'BOG', city: 'Bogotá', tz: 'America/Bogota' },
  { label: 'SAO', city: 'São Paulo', tz: 'America/Sao_Paulo' },
  { label: 'LON', city: 'London', tz: 'Europe/London' },
  { label: 'PAR', city: 'Paris', tz: 'Europe/Paris' },
  { label: 'BRU', city: 'Brussels', tz: 'Europe/Brussels' },
  { label: 'BER', city: 'Berlin', tz: 'Europe/Berlin' },
  { label: 'IST', city: 'Istanbul', tz: 'Europe/Istanbul' },
  { label: 'CAI', city: 'Cairo', tz: 'Africa/Cairo' },
  { label: 'RUH', city: 'Riyadh', tz: 'Asia/Riyadh' },
  { label: 'UAE', city: 'Dubai', tz: 'Asia/Dubai' },
  { label: 'ISB', city: 'Islamabad', tz: 'Asia/Karachi' },
  { label: 'DEL', city: 'New Delhi', tz: 'Asia/Kolkata' },
  { label: 'BKK', city: 'Bangkok', tz: 'Asia/Bangkok' },
  { label: 'BEI', city: 'Beijing', tz: 'Asia/Shanghai' },
  { label: 'SEO', city: 'Seoul', tz: 'Asia/Seoul' },
  { label: 'TYO', city: 'Tokyo', tz: 'Asia/Tokyo' },
  { label: 'SGP', city: 'Singapore', tz: 'Asia/Singapore' },
  { label: 'SYD', city: 'Sydney', tz: 'Australia/Sydney' },
];

function fmtHHMM(tz: string, now: Date): string {
  return now.toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function fmtHHMMSS(tz: string, now: Date): string {
  return now.toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function fmtDay(tz: string, now: Date): string {
  return now.toLocaleDateString('en-US', { timeZone: tz, weekday: 'short' }).toUpperCase();
}

function fmtDate(tz: string, now: Date): string {
  return now.toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' });
}

function getOffset(tz: string): string {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: tz,
    timeZoneName: 'shortOffset',
  }).formatToParts(new Date());
  const val = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'UTC';
  return val === 'GMT' ? 'UTC' : val.replace('GMT', 'UTC');
}

function getLocalHour(tz: string, now: Date): number {
  return Number.parseInt(
    now.toLocaleTimeString('en-US', { timeZone: tz, hour: '2-digit', hour12: false }),
    10,
  );
}

type BizStatus = 'open' | 'adjacent' | 'closed';

function bizStatus(tz: string, now: Date): BizStatus {
  const h = getLocalHour(tz, now);
  if (h >= 9 && h < 17) return 'open';
  if ((h >= 7 && h < 9) || (h >= 17 && h < 20)) return 'adjacent';
  return 'closed';
}

const STATUS_COLOR: Record<BizStatus, string> = {
  open: 'var(--green)',
  adjacent: 'var(--amber)',
  closed: 'var(--fg-4)',
};

// ── Hover card ──────────────────────────────────────────────────────────────

interface ClockCardProps {
  def: ClockDef;
  now: Date;
}

function ClockCard({ def, now }: ClockCardProps) {
  const status = bizStatus(def.tz, now);
  return (
    <div
      style={{
        padding: '10px 14px',
        borderRight: '1px solid var(--bd)',
        borderBottom: '1px solid var(--bd)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {/* Label row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1,
            color: 'var(--fg-1)',
          }}
        >
          {def.label}
        </span>
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: STATUS_COLOR[status],
            flexShrink: 0,
          }}
        />
      </div>

      {/* City */}
      <div style={{ fontSize: 10, color: 'var(--fg-4)', lineHeight: 1 }}>{def.city}</div>

      {/* Time */}
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 20,
          fontWeight: 300,
          color: 'var(--fg-1)',
          letterSpacing: -0.5,
          lineHeight: 1.15,
        }}
      >
        {fmtHHMM(def.tz, now)}
      </div>

      {/* Day · Date */}
      <div style={{ display: 'flex', gap: 5, fontSize: 10, color: 'var(--fg-3)' }}>
        <span>{fmtDay(def.tz, now)}</span>
        <span style={{ color: 'var(--fg-4)' }}>·</span>
        <span>{fmtDate(def.tz, now)}</span>
      </div>

      {/* Offset */}
      <div style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: 0.3, marginTop: 1 }}>
        {getOffset(def.tz)}
      </div>
    </div>
  );
}

interface HoverCardProps {
  now: Date;
  triggerRect: DOMRect;
}

function HoverCard({ now, triggerRect }: HoverCardProps) {
  const bottom = window.innerHeight - triggerRect.top + 8;
  const right = window.innerWidth - triggerRect.right;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        bottom,
        right,
        background: 'var(--bg-pane)',
        border: '1px solid var(--bd-strong)',
        boxShadow: '0 -12px 40px rgba(0,0,0,0.55)',
        zIndex: 9999,
        width: 660,
        userSelect: 'none',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '7px 14px',
          borderBottom: '1px solid var(--bd)',
          background: 'var(--bg-pane-2)',
        }}
      >
        <span
          style={{
            fontSize: 10,
            letterSpacing: 1.2,
            color: 'var(--fg-2)',
            fontWeight: 600,
          }}
        >
          WORLD CLOCKS
        </span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--fg-3)' }}>
          UTC&nbsp;&nbsp;{fmtHHMMSS('UTC', now)}
        </span>
      </div>

      {/* 5-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {GRID_CLOCKS.map((c) => (
          <ClockCard key={`${c.label}-${c.tz}`} def={c} now={now} />
        ))}
      </div>

      {/* Legend */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          padding: '6px 14px',
          borderTop: '1px solid var(--bd)',
          background: 'var(--bg-pane-2)',
          fontSize: 10,
          color: 'var(--fg-3)',
        }}
      >
        {(
          [
            ['open', 'var(--green)', '09:00 – 17:00'],
            ['adjacent', 'var(--amber)', '07:00 – 20:00'],
            ['closed', 'var(--fg-4)', 'off hours'],
          ] as const
        ).map(([key, color, label]) => (
          <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: color,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            {label}
          </span>
        ))}
      </div>
    </div>,
    document.body,
  );
}

// ── Main export ──────────────────────────────────────────────────────────────

export function WorldClocks() {
  const [now, setNow] = useState(() => new Date());
  const [open, setOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Tick every second so the hover-card UTC clock is live; bar only shows HH:MM
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const onEnter = useCallback(() => {
    if (containerRef.current) setTriggerRect(containerRef.current.getBoundingClientRect());
    setOpen(true);
  }, []);

  const onLeave = useCallback(() => setOpen(false), []);

  return (
    <div
      ref={containerRef}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'default' }}
    >
      {BAR_CLOCKS.map((c, i) => (
        <span key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {i > 0 && (
            <span style={{ color: 'var(--bd-strong)', userSelect: 'none', marginRight: 2 }}>·</span>
          )}
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 11,
              letterSpacing: 0.4,
              color: 'var(--fg-2)',
            }}
          >
            {c.label}
          </span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--fg-3)' }}>
            {fmtHHMM(c.tz, now)}
          </span>
        </span>
      ))}

      {open && triggerRect && <HoverCard now={now} triggerRect={triggerRect} />}
    </div>
  );
}
